// src/components/lesson/WelcomeTemplate.jsx

import React from "react";
import { useParams } from "react-router-dom";

import LessonShell from "./LessonShell";

const TEMP_OVERVIEW_IMAGE =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Gwin-Builder1.png";

function getLevelNumber(levelKey) {
  const match = String(levelKey || "").match(/(\d+)/);

  if (!match?.[1]) {
    return 1;
  }

  return Number(match[1]);
}

function resolveImageUrl(value) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (value?.asset?.url) {
    return value.asset.url;
  }

  if (value?.url) {
    return value.url;
  }

  return TEMP_OVERVIEW_IMAGE;
}

export default function WelcomeTemplate({
  /*
   * New content
   */
  levelHeading = "",
  levelOverviewText = "",

  /*
   * Existing compatibility
   */
  heading = "",
  introText = "",
  learningOutcomes = [],

  /*
   * Overview metadata
   */
  estimatedTime = "3–5 min",
  xpLabel = "xx XP",

  overviewImageSrc = "",
  overviewImageAlt = "Gwin mascot",

  /*
   * Navigation
   */
  startTo,
  startLabel = "Next",

  questNumber,
  levelNumber,

  backTo,
  backLabel = "Back",

  progressStep = 1,
  progressTotal = 11,
}) {
  const { levelKey } = useParams();

  const resolvedLevelNumber =
    levelNumber || getLevelNumber(levelKey);

  const displayHeading =
    levelHeading || heading || "Level Overview";

  const displayOverview =
    levelOverviewText || introText || "";

  const resolvedOverviewImageSrc =
    resolveImageUrl(overviewImageSrc);

  return (
    <LessonShell
      progressStep={progressStep}
      progressTotal={progressTotal}
      showBack
      backLabel={backLabel}
      backTo={backTo}
      showNext
      nextLabel={startLabel}
      nextTo={startTo}
      contentClassName="pt-8"
    >
      {/*
       * LEVEL NUMBER
       */}
      <p
        className="
          text-[15px]
          font-bold
          uppercase
          leading-none
          text-[#FF914D]
          sm:text-[17px]
        "
      >
        Level{" "}
        {resolvedLevelNumber}
      </p>

      {/*
       * MAIN TITLE
       */}
      <h1
        className="
          mt-6
          max-w-[520px]
          text-[34px]
          font-extrabold
          leading-[1.07]
          tracking-[-0.035em]
          text-[#111111]
          sm:mt-7
          sm:text-[50px]
        "
      >
        {displayHeading}
      </h1>

      {/*
       * TIME + XP
       */}
      <div
        className="
          mt-7
          flex
          flex-wrap
          items-center
          gap-4
        "
      >
        {!!estimatedTime && (
          <span
            className="
              inline-flex
              min-w-[94px]
              items-center
              justify-center
              rounded-full
              bg-[#F1F1F3]
              px-5
              py-2
              text-[14px]
              font-semibold
              text-[#151515]
            "
          >
            {estimatedTime}
          </span>
        )}

        {!!xpLabel && (
          <span
            className="
              inline-flex
              min-w-[94px]
              items-center
              justify-center
              rounded-full
              bg-[#F1F1F3]
              px-5
              py-2
              text-[14px]
              font-semibold
              text-[#151515]
            "
          >
            {xpLabel}
          </span>
        )}
      </div>

      {/*
       * MASCOT + SPEECH BUBBLE
       */}
      <section
        className="
          relative
          mx-auto
          mt-10
          grid
          min-h-[300px]
          w-full
          max-w-[570px]
          grid-cols-[38%_1fr]
          items-end
          gap-4
          sm:mt-12
          sm:min-h-[355px]
        "
      >
        <div
          className="
            flex
            h-full
            items-end
            justify-center
          "
        >
          <img
            src={resolvedOverviewImageSrc}
            alt={overviewImageAlt}
            className="
              h-auto
              w-full
              max-w-[180px]
              object-contain
              sm:max-w-[245px]
            "
            loading="eager"
            decoding="async"
            draggable="false"
          />
        </div>

        {!!displayOverview && (
          <div
            className="
              relative
              mb-24
              self-center
              rounded-[24px]
              border-2
              border-[#FF914D]
              bg-[#FFD8BF]
              px-5
              py-5
              text-center
              sm:mb-28
              sm:-translate-y-6
              sm:px-7
              sm:py-6
            "
          >
            <p
              className="
                relative
                z-10
                text-[14px]
                font-medium
                leading-[1.45]
                text-[#151515]
                sm:text-[16px]
                sm:leading-[1.5]
              "
            >
              {displayOverview}
            </p>

            {/*
             * Clean SVG speech bubble pointer.
             * The filled top edge overlaps into the bubble slightly,
             * hiding the bubble border seam without a cover strip.
             */}
            <svg
              className="
                pointer-events-none
                absolute
                -bottom-[36px]
                left-[18px]
                z-20
                h-[44px]
                w-[56px]
                sm:left-[26px]
              "
              viewBox="0 0 56 44"
              aria-hidden="true"
            >
              <polygon
                points="7,0 63,0 17,35"
                fill="#FFD8BF"
              />

              <path
                d="M8 9 L17 35 L51 9"
                fill="none"
                stroke="#FF914D"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </section>

      {/*
       * Old learning outcomes deliberately remain hidden in this redesign.
       */}
      {false &&
        Array.isArray(learningOutcomes) &&
        learningOutcomes.length > 0 && (
          <div>
            {learningOutcomes.join(", ")}
          </div>
        )}
    </LessonShell>
  );
}